import React, { useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dimmer, Grid, Label, LabelDetail, Popup, SemanticWIDTHS, Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import cloneDeep from 'lodash.clonedeep'
import { StyledFirstColumn } from '../style'
import CustomIcon from '~/components/CustomIcon'
import { formatNumber } from '~/utils/helpers'
import { getSumOfFilledFeeds, getTransformCowsFeedGroups, prepareDataForPayload } from '../../utils'
import { StyledEnergyWrapper, StyledGridColumn, StyledLoader, StyledTable } from './style'
import BarleyModal from './BarleyModal'
import { ClimaCheckOptionsType } from '~/api/services/climaCheck'
import ClimaCheckQuery from '~/QueryHooks/ClimaChQuery'
import { useParams } from 'react-router-dom'
import InfoPopup from '~/components/InfoPopup'
import { FeedPageContext } from '../..'

export const monthsArray = [
  { key: 1, label: '1 Monat' },
  { key: 2, label: '2 Monate' },
  { key: 3, label: '3 Monate' },
  { key: 4, label: '4 Monate' },
  { key: 5, label: '5 Monate' },
  { key: 6, label: '6 Monate' },
  { key: 7, label: '7 Monate' },
  { key: 8, label: '8 Monate' },
  { key: 9, label: '9 Monate' },
  { key: 10, label: '10 Monate' },
  { key: 11, label: '11 Monate' }
];

const JUICY_FEEDS_KEY = 'juicyFeeds'

interface Props {
  milkOptions: ClimaCheckOptionsType
}

const TwelveStep: React.FC<Props> = (props) => {
  const { milkOptions } = props
  const { id } = useParams()
  const { control, getValues, setValue } = useFormContext()
  const { t } = useTranslation()

  const { energyCalcData, setEnergyCalcData, loadingEnergyData, setIsEnergyDataFetching } = useContext(FeedPageContext)

  const energyLabels = [
    {
      name: t('Conservation needs'),
      key: 'maintenanceNeedsEnergy'
    },
    {
      name: t('Reproductive needs'),
      key: 'reproductionNeedsEnergy'
    },
    {
      name: t('Power requirement'),
      key: 'powerRequirementEnergy'
    }
  ]

  const panesOptions = [
    {
      title: t('Coarse Feeds'),
      key: 'coarseFeeds'
    },
    {
      title: t('Power and mineral feed'),
      key: 'concentratedFeeds'
    },
    {
      title: t('Juicy Feeds'),
      key: 'juicyFeeds'
    }
  ]

  const watchPhaseEnabled = useWatch({ control, name: 'phaseFeeding' })
  const feedGroups = useWatch({ control, name: 'feedGroups' })

  const {
    data: energyData,
    isFetching: isFetchingData
  } = ClimaCheckQuery.getClimaChEnergyData(
    id,
    'cows',
    prepareDataForPayload(getValues()),
  )

  useEffect(() => {
    setIsEnergyDataFetching(isFetchingData)
  }, [isFetchingData])

  const [feedGroupsData, setFeedGroupsData] = useState([])

  useEffect(() => {
    if (energyCalcData) {
      setEnergyCalcData(null)
    }
  }, [feedGroups, watchPhaseEnabled])

  useEffect(() => {
    if (watchPhaseEnabled) {
      setFeedGroupsData(getTransformCowsFeedGroups(feedGroups))
    } else {
      setFeedGroupsData(feedGroups)
    }
  }, [watchPhaseEnabled])

  const [parsedData, setParsedData] = useState(null)
  const [barleyData, setBarleyData] = useState(null)

  function getParsedData(data) {
    const result = {
      coarseFeeds: [],
      concentratedFeeds: [],
      juicyFeeds: []
    };

    if (watchPhaseEnabled) {
      data = getTransformCowsFeedGroups(data)
    }

    data.forEach(item => {
      item.concentratedFeeds.forEach(feed => {
        if (!result.concentratedFeeds.find(item => item.id === feed.id)) {
          const concentratedFeeds = getValues('cowsConcentratedFeeds')
          const index = concentratedFeeds?.findIndex(concentratedFeed => concentratedFeed.id === feed.id)
          result.concentratedFeeds.push({
            ...feed,
            dryMatter: concentratedFeeds[index]?.dryMatter,
            energy: concentratedFeeds[index]?.energy,
            rawAsh: concentratedFeeds[index]?.rawAsh,
            rawProtein: concentratedFeeds[index]?.rawProtein,
            rawFat: concentratedFeeds[index]?.rawFat,
            rawFiber: concentratedFeeds[index]?.rawFiber,
            index
          });
        }
      });
      item.juicyFeeds.forEach(feed => {
        if (!result.juicyFeeds.find(item => item.id === feed.id)) {
          const juicyFeeds = getValues('juicyFeeds')
          const index = juicyFeeds?.findIndex(juicyFeed => juicyFeed.id === feed.id)
          result.juicyFeeds.push({
            ...feed,
            dryMatter: juicyFeeds[index]?.dryMatter,
            energy: juicyFeeds[index]?.energy, index,
            juicyFeedId: juicyFeeds[index].juicyFeedId
          });
        }
      });
      item.coarseFeeds.forEach(feed => {
        if (!result.coarseFeeds.find(item => item.id === feed.id)) {
          const coarseFeeds = getValues('cowsCoarseFeeds')
          const index = coarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
          result.coarseFeeds.push({
            ...feed,
            dryMatter: coarseFeeds[index]?.dryMatter,
            energy: coarseFeeds[index]?.energy,
            rawAsh: coarseFeeds[index]?.rawAsh,
            rawProtein: coarseFeeds[index]?.rawProtein,
            rawFat: coarseFeeds[index]?.rawFat,
            rawFiber: coarseFeeds[index]?.rawFiber,
            index
          });
        }
      });
    });
    return result
  }

  useEffect(() => {
    setParsedData(getParsedData(cloneDeep(feedGroups)))
  }, [])

  function renderTableHeaders(firstColumnTitle) {
    return <Table.Row>
      <Table.HeaderCell width={'4'}>{firstColumnTitle}</Table.HeaderCell>
      {feedGroupsData.map(feedGroup => {
        const month = feedGroup?.months ? monthsArray.find(month => month.key === feedGroup?.months) : ''
        return <Table.HeaderCell key={feedGroup.groupKey}>
          {`${feedGroup?.name || ''} ${month ? `(${month?.label})` : ''}`}
        </Table.HeaderCell>
      })}
    </Table.Row>
  }

  function getCowsFeedKey(key: string) {
    switch (key) {
      case 'coarseFeeds':
        return 'cowsCoarseFeeds';
      case 'concentratedFeeds':
        return 'cowsConcentratedFeeds';
      case 'juicyFeeds':
        return 'juicyFeeds'
      default:
        return 'juicyFeeds'
    }
  }

  function renderTableBody(rows, key) {
    return <Table.Body>
      {rows.map((feed) => {
        const cowsFeedKey = getCowsFeedKey(key)
        const feedData = getValues(cowsFeedKey)[feed.index]
        return <Table.Row key={feed.id}>
          <Table.Cell>
            <div>{feed.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <small style={{ fontSize: '85%' }}>
                {t('[TM: {{dryMatter}} % Energy: {{energy}} MJ NEL/kg TM]',
                  {
                    dryMatter: formatNumber(getValues(cowsFeedKey)[feed.index]?.dryMatter?.replace(',', '.'), 2),
                    energy: formatNumber(getValues(cowsFeedKey)[feed.index]?.energy?.replace(',', '.'), 2)
                  }
                )}
              </small>
              {feed?.dryMatter && feed?.energy && !feed.juicyFeedId && <div style={{ display: 'flex', gap: 5, marginLeft: 7 }}>
                <InfoPopup
                  position={'top center'}
                  styleIcon={{ fontSize: '1.2em' }}
                  content={<small style={{ fontSize: '85%' }}>
                    <div>{`TM: ${formatNumber(feedData?.dryMatter?.replace(',', '.'), 2)} MJ NEL/kg TM`}</div>
                    <div>{`Energie: ${formatNumber(feedData?.energy?.replace(',', '.'), 2)} MJ NEL/kg TM`}</div>
                    <div>{`Rohasche: ${formatNumber(feedData?.rawAsh?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                    <div>{`Rohprotein: ${formatNumber(feedData?.rawProtein?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                    <div>{`Rohfett: ${formatNumber(feedData?.rawFat?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                    <div>{`Rohfaser: ${formatNumber(feedData?.rawFiber?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                  </small>}
                />
                <Popup
                  content={<div>
                    <div>{t('Change')}</div>
                  </div>}
                  trigger={<CustomIcon
                    name={'pencil'}
                    onClick={() => {
                      const barleyValue = getValues(`${cowsFeedKey}[${feed.index}]`)
                      setBarleyData({
                        ...feedData,
                        ...barleyValue,
                        index: feed.index,
                        key: cowsFeedKey,
                        feedName: feed.label,
                        isJuicyFeed: cowsFeedKey === JUICY_FEEDS_KEY
                      })
                    }}
                    style={{ fontSize: '1.2em' }}
                  />}
                />
              </div>}
            </div>
          </Table.Cell>
          {feedGroupsData.map((feedGroup, indexFeedGrup) => {
            const indexFeed = feedGroup[key].findIndex(value => value.id === feed.id)
            const disabled = indexFeed < 0
            const feedingPhase = getValues(`feedGroups[${feedGroup.feedGroupIndex}].feedingPhases`)
            const phaseFeedingName = watchPhaseEnabled && feedingPhase?.length > 0 ? `feedGroups[${feedGroup.feedGroupIndex}].feedingPhases[${feedGroup.order - 1}].${key}[${indexFeed}].value` : ''
            const indexFeedWithPhase = watchPhaseEnabled ? feedGroup.feedGroupIndex : indexFeedGrup
            const name = watchPhaseEnabled && feedingPhase?.length > 0 ? phaseFeedingName : `feedGroups[${indexFeedWithPhase}].${key}[${indexFeed}].value`

            return <Table.Cell width={Math.floor(12 / feedGroupsData.length) as SemanticWIDTHS} key={name}>
              {!disabled && <FormFields.Input
                disabled={indexFeed < 0}
                placeholder={t('kg FM/animal and day')}
                name={name}
                control={control}
                typeInput={'controlled-inline-error'}
                withoutAsterisk
                rules={{
                  required: true,
                  validate: (value) => {
                    const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                    if (feedData.limitMaxValue && transformValue > feedData.limitMaxValue) {
                      return `Der bei ${feedData.label} eingegebene Wert liegt oberhalb des Maximalwerts von ${formatNumber(feedData.limitMaxValue, 2)} kg. Bitte korrigieren Sie Ihre Eingabe.`
                    }
                    return true
                  }
                }}
                warningMax={{
                  value: feedData?.warningMaxValue,
                  message: `Der bei ${feedData.label} eingegebene Wert liegt außerhalb des Normalbereichs von maximal ${formatNumber(feedData?.warningMaxValue, 2)} kg. 
                  Wenn Sie sich sicher sind, dass Ihre Eingabe korrekt ist, können Sie diese Meldung ignorieren.`
                }}
                suffix={' kg FM/Tier u. Tag'}
                noErrorMessage
                allowDecimals
                decimalScale={2}
              />
              }
            </Table.Cell>
          })}
        </Table.Row>
      })}
    </Table.Body>
  }

  function renderTable(rows, key, firstColumnTitle) {
    return rows?.length > 0 && <StyledTable>
      <Table.Header>
        {renderTableHeaders(firstColumnTitle)}
      </Table.Header>
      {renderTableBody(rows, key)}
    </StyledTable>
  }

  function parseConcentratedFeeds(concentratedFeeds) {
    return concentratedFeeds.reduce((acc, item) => {
      const { group } = item;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});
  }

  function getConcentratedFeedTitle(groupIndex) {
    return Object.keys(milkOptions.cowsConcentratedFeeds)[groupIndex - 1]
  }

  function renderFeedRations() {
    return Object.keys(parsedData).map(key => {
      const title = panesOptions.find(pane => pane.key === key)?.title
      if (key === 'concentratedFeeds') {
        const groupConcentratedFeeds = parseConcentratedFeeds(parsedData?.concentratedFeeds)
        return <div style={{ margin: '1em 0em' }} key={key}>
          <div>
            {Object.keys(groupConcentratedFeeds).map(key => {
              const firstColumnTitle = <StyledFirstColumn>
                <div>{title}</div>
                <div className='subHeader'>{getConcentratedFeedTitle(key)}</div>
              </StyledFirstColumn>
              return <div style={{ margin: '1em 0em' }} key={groupConcentratedFeeds[key]}>
                {renderTable(Object.values(groupConcentratedFeeds[key]), 'concentratedFeeds', firstColumnTitle)}
              </div>
            })}
          </div>
        </div>
      } else {
        return <div style={{ margin: '1em 0em' }} key={key}>
          {renderTable(Object.values(parsedData[key]), key, title)}
        </div>
      }
    })
  }

  function renderBarleyModal() {
    return <BarleyModal
      barleyData={barleyData}
      handleClose={(data) => {
        if (data && barleyData) {
          const value = getValues(`${barleyData?.key}[${barleyData.index}]`)
          setValue(`${barleyData?.key}[${barleyData.index}]`, { ...value, ...data })
        }
        setBarleyData(null)
      }}
    />
  }

  function renderTotal() {
    const sums = feedGroupsData.map(feedGroup => getSumOfFilledFeeds(feedGroup))
    return <StyledTable>
      <Table.Header>
        <Table.HeaderCell width={'4'}>{t('Total')}</Table.HeaderCell>
        {sums.map((sum) => {
          return <Table.Cell width={Math.floor(12 / feedGroupsData.length) as SemanticWIDTHS} key={`sum${Math.random()}`}>
            <div style={{ marginLeft: 14 }}>
              {`${formatNumber(sum, 2)} kg FM/Tier u. Tag`}
            </div>
          </Table.Cell>
        })}
      </Table.Header>
    </StyledTable>
  }

  function renderSpinner() {
    return <Dimmer inverted active={true} style={{ background: 'transparent' }}>
      <StyledLoader active={true} />
    </Dimmer>
  }

  function renderEnergyNeeds() {
    return <Grid columns={2}>
      {energyLabels.map(energyLabel => {
        const value = energyData ? energyData[energyLabel.key] : ''
        return <Grid.Row style={{ fontWeight: 'bold', paddingTop: '0.3rem', paddingBottom: '0.3rem' }} key={energyLabel.name} >
          <Grid.Column>{energyLabel.name}:</Grid.Column>
          <StyledGridColumn>{isFetchingData ? renderSpinner() : `${formatNumber(value, 0)} MJ NEL/Jahr`}</StyledGridColumn>
        </Grid.Row>
      })}
    </Grid>
  }

  function renderTotalEnergy() {
    return <StyledEnergyWrapper>
      {energyCalcData?.totalEnergyWithAddition && <Label as='a'>
        {t('Calculated feed energy')}:
        <LabelDetail style={{ position: 'relative', minWidth: '1.3rem' }}>
          {loadingEnergyData ? renderSpinner() : `${formatNumber(energyCalcData?.totalEnergyWithAddition, 0)} MJ NEL/Jahr`}
        </LabelDetail>
      </Label>}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Label as='a'>
          {t('Total energy requirement')}:
          <LabelDetail style={{ position: 'relative', minWidth: '1.3rem' }}>
            {isFetchingData ? renderSpinner() : `${formatNumber(energyData?.totalEnergy, 0)} MJ NEL/Jahr`}
          </LabelDetail>
        </Label>
        <InfoPopup
          content={renderEnergyNeeds()}
          styleIcon={{ fontSize: '1.5em', marginLeft: 5 }}
          position={'top left'}
          style={{ minWidth: 350 }}
        /></div>
    </StyledEnergyWrapper>
  }

  return <div>
    {renderTotalEnergy()}
    <div>
      {parsedData && renderFeedRations()}
      {renderTotal()}
      {renderBarleyModal()}
    </div>
  </div>
}
export default TwelveStep